import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { LifeBuoy, Plus, Send, ChevronLeft, MessageSquare } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";

const EASE = [0.16, 1, 0.3, 1];
const inputCls = "w-full bg-[#050505] border border-white/10 px-3 py-2.5 font-mono text-sm text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-blue focus:border-nc-blue";
const labelCls = "font-mono text-[10px] tracking-[0.25em] text-zinc-500 uppercase block mb-2";
const CATEGORIES = [["general", "General"], ["quote", "Quote / Project"], ["technical", "Technical Issue"], ["report-user", "Report a User"]];
const STATUS_CLS = { open: "text-nc-blue border-nc-blue/40 bg-nc-blue/10", answered: "text-nc-green border-nc-green/40 bg-nc-green/10", closed: "text-zinc-500 border-white/15 bg-white/5" };

export default function SupportPage() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [open, setOpen] = useState(null);
  const [mode, setMode] = useState("list"); // list | new | view
  const [form, setForm] = useState({ subject: "", category: "general", message: "" });
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(() => {
    api.get("/support/tickets").then((r) => setTickets(r.data)).catch(() => { navigate("/auth"); });
  }, [navigate]);

  useEffect(() => {
    const u = (() => { try { return JSON.parse(localStorage.getItem("nc_user")); } catch { return null; } })();
    if (!u) { navigate("/auth"); return; }
    load();
  }, [load, navigate]);

  const create = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/support/tickets", form);
      toast.success("Ticket submitted — support will reply here");
      setForm({ subject: "", category: "general", message: "" });
      setMode("list");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const openTicket = async (id) => {
    const r = await api.get(`/support/tickets/${id}`);
    setOpen(r.data);
    setMode("view");
  };

  const sendReply = async () => {
    setSending(true);
    try {
      await api.post(`/support/tickets/${open.id}/reply`, { message: reply });
      setReply("");
      await openTicket(open.id);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="support-page" className="min-h-screen bg-[#050505] text-zinc-100 flex flex-col selection:bg-nc-blue/30">
      <SiteNav />
      <header className="border-b border-white/10 relative overflow-hidden">
        <div className="absolute inset-0 grid-texture opacity-50" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-12 md:py-16">
          <p className="font-mono text-xs tracking-[0.4em] text-nc-orange uppercase mb-3">Help Desk // Studio Support</p>
          <h1 data-testid="support-title" className="font-sans font-bold uppercase tracking-tight text-3xl sm:text-4xl text-zinc-50">Support Tickets</h1>
          <p className="font-mono text-xs text-zinc-500 mt-3 max-w-xl leading-relaxed">
            Direct line to the studio. Open a ticket and track the conversation right here — no email needed.
          </p>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10 w-full">
        {mode === "list" && (
          <div className="flex flex-col gap-4">
            <button data-testid="support-new-button" onClick={() => setMode("new")} className="self-start bg-nc-blue hover:bg-nc-blue/80 text-white font-sans font-semibold uppercase tracking-[0.2em] text-xs px-5 py-3 flex items-center gap-2 transition-colors">
              <Plus className="w-4 h-4" /> New Ticket
            </button>
            {tickets.length === 0 && (
              <div className="border border-dashed border-white/15 p-14 text-center">
                <LifeBuoy className="w-8 h-8 text-zinc-700 mx-auto mb-3" />
                <p className="font-mono text-xs text-zinc-500">No tickets yet. We're here when you need us.</p>
              </div>
            )}
            {tickets.map((t, i) => (
              <motion.button
                key={t.id}
                data-testid={`support-ticket-${i + 1}`}
                onClick={() => openTicket(t.id)}
                className="text-left border border-white/10 bg-black/40 p-5 hover:border-nc-blue/50 transition-colors duration-300"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.05, ease: EASE }}
              >
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div className="flex items-center gap-3 min-w-0">
                    <MessageSquare className="w-4 h-4 text-nc-blue shrink-0" />
                    <p className="font-sans font-semibold text-sm text-zinc-100 truncate">{t.subject}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`font-mono text-[9px] tracking-[0.2em] uppercase border px-2 py-0.5 ${STATUS_CLS[t.status]}`}>{t.status}</span>
                    <span className="font-mono text-[10px] text-zinc-600">{String(t.updatedAt).slice(0, 10)}</span>
                  </div>
                </div>
                <p className="font-mono text-[10px] text-zinc-600 mt-2">{t.category} // {t.messages.length} message{t.messages.length === 1 ? "" : "s"}</p>
              </motion.button>
            ))}
          </div>
        )}

        {mode === "new" && (
          <motion.form data-testid="support-form" onSubmit={create} className="border border-white/10 bg-black/40 p-6 md:p-8 flex flex-col gap-5 max-w-2xl" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: EASE }}>
            <button type="button" onClick={() => setMode("list")} className="self-start font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500 hover:text-zinc-100 flex items-center gap-2 transition-colors">
              <ChevronLeft className="w-3.5 h-3.5" /> Back to tickets
            </button>
            <h2 className="font-sans font-bold uppercase text-lg text-zinc-50">New Support Ticket</h2>
            <div>
              <label className={labelCls}>Category</label>
              <select data-testid="support-category-select" className={inputCls} value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}>
                {CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Subject *</label>
              <input data-testid="support-subject-input" required minLength={5} className={inputCls} value={form.subject} onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))} placeholder="One line summary" />
            </div>
            <div>
              <label className={labelCls}>Details *</label>
              <textarea data-testid="support-message-input" required minLength={10} rows={6} className={`${inputCls} resize-none`} value={form.message} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))} placeholder="Describe the issue — include links, screenshots references, or your quote reference if relevant." />
            </div>
            <button data-testid="support-submit-button" type="submit" disabled={sending} className="self-start bg-nc-blue hover:bg-nc-blue/80 disabled:opacity-50 text-white font-sans font-semibold uppercase tracking-[0.2em] text-xs px-6 py-3 flex items-center gap-2 transition-colors">
              <Send className="w-4 h-4" /> {sending ? "Sending..." : "Submit Ticket"}
            </button>
          </motion.form>
        )}

        {mode === "view" && open && (
          <div className="flex flex-col gap-5 max-w-3xl">
            <button data-testid="support-back-button" onClick={() => { setMode("list"); setOpen(null); }} className="self-start font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500 hover:text-zinc-100 flex items-center gap-2 transition-colors">
              <ChevronLeft className="w-3.5 h-3.5" /> Back to tickets
            </button>
            <div className="border border-white/10 bg-black/40 p-6">
              <div className="flex items-center justify-between gap-4 flex-wrap border-b border-white/10 pb-4 mb-5">
                <div>
                  <h2 data-testid="support-ticket-title" className="font-sans font-bold uppercase text-lg text-zinc-50">{open.subject}</h2>
                  <p className="font-mono text-[10px] text-zinc-600 mt-1">{open.category} // opened {String(open.createdAt).slice(0, 10)}</p>
                </div>
                <span className={`font-mono text-[9px] tracking-[0.2em] uppercase border px-2 py-1 ${STATUS_CLS[open.status]}`}>{open.status}</span>
              </div>
              <div className="flex flex-col gap-4" data-testid="support-thread">
                {open.messages.map((m, i) => (
                  <div key={i} className={`border p-4 ${m.role === "admin" ? "border-nc-orange/30 bg-nc-orange/5 ml-6" : "border-white/10 bg-[#050505]/60 mr-6"}`}>
                    <p className="font-mono text-[9px] tracking-[0.2em] uppercase mb-1.5">
                      <span className={m.role === "admin" ? "text-nc-orange" : "text-nc-blue"}>{m.role === "admin" ? "Studio Support" : m.from}</span>
                      <span className="text-zinc-600"> // {String(m.at).slice(0, 16).replace("T", " ")}</span>
                    </p>
                    <p className="font-mono text-xs text-zinc-300 leading-relaxed whitespace-pre-wrap">{m.message}</p>
                  </div>
                ))}
              </div>
              {open.status !== "closed" && (
                <div className="flex gap-2 mt-5 border-t border-white/10 pt-5">
                  <input data-testid="support-reply-input" className={inputCls} placeholder="Write a reply…" value={reply} onChange={(e) => setReply(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && reply.trim()) sendReply(); }} />
                  <button data-testid="support-reply-button" onClick={sendReply} disabled={sending || !reply.trim()} className="bg-nc-blue hover:bg-nc-blue/80 disabled:opacity-40 text-white px-5 flex items-center transition-colors">
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      <SiteFooter />
    </div>
  );
}
